import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { jobs } from '../lib/data';
import { JobCard } from '../components/jobs/JobCard';
import { JobFilters, Filters } from '../components/jobs/JobFilters';
import { Eyebrow } from '../components/ui/Reveal';
const emptyFilters: Filters = {
  disciplines: [],
  types: [],
  remote: []
};
const sortOptions = ['Most recent', 'Discipline A–Z', 'Company A–Z'] as const;
export function Opportunities() {
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') || '');
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [sort, setSort] = useState<(typeof sortOptions)[number]>('Most recent');
  const [mobileOpen, setMobileOpen] = useState(false);
  const results = useMemo(() => {
    let list = jobs.filter(job => {
      const q = query.trim().toLowerCase();
      const matchesQuery = !q || job.title.toLowerCase().includes(q) || job.company.toLowerCase().includes(q) || job.discipline.toLowerCase().includes(q) || job.location.toLowerCase().includes(q);
      const matchesDiscipline = !filters.disciplines.length || filters.disciplines.includes(job.discipline);
      const matchesType = !filters.types.length || filters.types.includes(job.type);
      const matchesRemote = !filters.remote.length || filters.remote.includes(job.remote);
      return matchesQuery && matchesDiscipline && matchesType && matchesRemote;
    });
    list = [...list].sort((a, b) => {
      if (sort === 'Most recent') return a.postedDays - b.postedDays;
      if (sort === 'Discipline A–Z') return a.discipline.localeCompare(b.discipline);
      return a.company.localeCompare(b.company);
    });
    return list;
  }, [query, filters, sort]);
  return <div className="mx-auto max-w-7xl px-6 py-12 lg:px-10 lg:py-16" data-id="element-571" data-mp="3nuuxz5qkyg1">
      <div className="max-w-2xl" data-id="element-572" data-mp="bpvhc43uqq0x">
        <Eyebrow data-id="element-573" data-mp="bosmxm7bm5g9">Current mandates</Eyebrow>
        <h1 className="mt-6 font-serif text-4xl leading-tight text-ink sm:text-5xl" data-id="element-574" data-mp="fqxi4pdf3a50">
          Opportunities
        </h1>
        <p className="mt-4 text-lg leading-relaxed text-ink-600" data-id="element-575" data-mp="kpqtoc958r2j">
          Discreet, senior mandates from boards and founders around the world.
        </p>
      </div>

      <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-576" data-mp="3o6qx2zs3odjy">
        <div className="flex flex-1 items-center rounded-full border border-ink/15 bg-ivory-50 px-4 shadow-soft focus-within:border-gold" data-id="element-577" data-mp="3o4nwxmx8h41i">
          <SearchIcon className="h-5 w-5 shrink-0 text-ink-500" data-id="element-578" data-mp="1z0ryqvb7ab9" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search roles, disciplines, companies…" aria-label="Search opportunities" className="w-full bg-transparent px-3 py-3 text-sm text-ink placeholder:text-ink-400 focus:outline-none" data-id="element-579" data-mp="1xpneou1hyj3" />
          {query && <button onClick={() => setQuery('')} aria-label="Clear search" data-id="element-580" data-mp="32ui8z6rmyooe">
              <XIcon className="h-4 w-4 text-ink-400 hover:text-ink" data-id="element-581" data-mp="2xwgpwlu7mpcx" />
            </button>}
        </div>
        <div className="flex items-center gap-3" data-id="element-582" data-mp="2swm1coxwqbv1">
          <label className="hidden items-center gap-2 text-sm text-ink-500 sm:flex" data-id="element-583" data-mp="2szggc0y5j4h2">
            Sort
            <select value={sort} onChange={e => setSort(e.target.value as (typeof sortOptions)[number])} className="rounded-full border border-ink/15 bg-ivory-50 px-4 py-2.5 text-sm text-ink focus:border-gold focus:outline-none" data-id="element-584" data-mp="2jwyv9pu8ghvn">
              {sortOptions.map(o => <option key={o} data-id="element-585" data-mp="258ubeijg20ba">{o}</option>)}
            </select>
          </label>
          <button onClick={() => setMobileOpen(true)} className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-ivory-50 px-4 py-2.5 text-sm text-ink lg:hidden" data-id="element-586" data-mp="2097y693m8k2h">
            <SlidersHorizontalIcon className="h-4 w-4" data-id="element-587" data-mp="2kzbumlobw14s" /> Filters
          </button>
        </div>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-12" data-id="element-588" data-mp="2pwlt0qtp3n4r">
        <aside className="hidden lg:col-span-3 lg:block" data-id="element-589" data-mp="2c5yx16fbkbcd">
          <div className="sticky top-24" data-id="element-590" data-mp="2c5grtkaoimsb">
            <JobFilters filters={filters} onChange={setFilters} onClear={() => setFilters(emptyFilters)} data-id="element-591" data-mp="2g6sh1opn55zz" />
          </div>
        </aside>

        <div className="lg:col-span-9" data-id="element-592" data-mp="276cimtnr8p1z">
          <p className="mb-6 text-sm text-ink-500" data-id="element-593" data-mp="277f60rlvc9d5">
            {results.length} {results.length === 1 ? 'opportunity' : 'opportunities'}
          </p>
          {results.length === 0 ? <div className="rounded-2xl border border-dashed border-ink/20 bg-ivory-50 py-20 text-center" data-id="element-594" data-mp="1x9hmgheyf0qv">
              <p className="font-serif text-xl text-ink" data-id="element-595" data-mp="3nm6z76qu9kni">No matching mandates</p>
              <p className="mt-2 text-sm text-ink-500" data-id="element-596" data-mp="3rmqu03nxwc6h">
                Try adjusting your search or filters.
              </p>
            </div> : <div className="grid gap-6 md:grid-cols-2" data-id="element-597" data-mp="1bxin68nstczf">
              <AnimatePresence mode="popLayout" data-id="element-598" data-mp="1bytswe0t0ut6">
                {results.map(job => <motion.div key={job.id} layout initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.97
            }} transition={{
              duration: 0.35,
              ease: [0.16, 1, 0.3, 1]
            }} data-id="element-599" data-mp="1ppp3pvuv5lze">
                    <JobCard job={job} data-id="element-600" data-mp="1uqcuqhj3e2tm" />
                  </motion.div>)}
              </AnimatePresence>
            </div>}
        </div>
      </div>

      <AnimatePresence data-id="element-601" data-mp="23pholajpl7zg">
        {mobileOpen && <motion.div className="fixed inset-0 z-[60] bg-ink/40 backdrop-blur-sm lg:hidden" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setMobileOpen(false)} data-id="element-602" data-mp="e98jlp8d6h67">
            <motion.div className="absolute inset-y-0 right-0 w-[85%] max-w-sm overflow-y-auto bg-ivory-100 p-4" initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          duration: 0.35,
          ease: [0.16, 1, 0.3, 1]
        }} onClick={e => e.stopPropagation()} data-id="element-603" data-mp="2ebzyexw1268o">
              <div className="mb-4 flex items-center justify-between px-2" data-id="element-604" data-mp="2sbiuutfh7t55">
                <span className="font-serif text-lg text-ink" data-id="element-605" data-mp="2x9kou9efxcve">Filters</span>
                <button onClick={() => setMobileOpen(false)} aria-label="Close filters" className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-ink/5" data-id="element-606" data-mp="3212gg6ukq13p">
                  <XIcon className="h-5 w-5 text-ink" data-id="element-607" data-mp="3b43k80vyvg47" />
                </button>
              </div>
              <JobFilters filters={filters} onChange={setFilters} onClear={() => setFilters(emptyFilters)} data-id="element-608" data-mp="3g1mxaxgxhmuk" />
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}